import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { FeedbackProvider, useFeedback, type ConfirmResult, type ConfirmSpec, type ToastSpec } from '@vello/web-theme';
import { useT } from './i18n/provider';

/**
 * The console's chrome state (D319 web-shell standard): the collapsible sidebar (persisted per browser), the
 * toast and confirm services every screen calls, the ⌘K palette state, and a one-shot search handoff from the
 * palette to a directory screen. Screens never mount their own toast/dialog — they call these.
 */
const SIDEBAR_KEY = 'vello.admin.sidebarCollapsed';

export interface ConsoleValue {
  readonly sidebarCollapsed: boolean;
  readonly toggleSidebar: () => void;
  readonly toast: (spec: ToastSpec) => void;
  readonly confirm: (spec: ConfirmSpec) => Promise<ConfirmResult>;
  readonly paletteOpen: boolean;
  readonly openPalette: () => void;
  readonly closePalette: () => void;
  /** A search the palette hands to the next directory screen (consumed once). */
  readonly takeDirectoryQuery: () => string | undefined;
  readonly setDirectoryQuery: (q: string) => void;
  /** A record screen names its record for the breadcrumb (cleared on unmount). */
  readonly recordTitle: string | undefined;
  readonly setRecordTitle: (title: string | undefined) => void;
}

const ConsoleContext = createContext<ConsoleValue | undefined>(undefined);

function loadCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch {
    return false;
  }
}

/** The console services over the shared `FeedbackProvider` (confirm + toast): one implementation for every console. */
export function ConsoleProvider({ children }: { children: ReactNode }): ReactElement {
  const { t } = useT();
  return (
    <FeedbackProvider closeLabel={t('ui.close')} cancelLabel={t('ui.cancel')}>
      <ConsoleState>{children}</ConsoleState>
    </FeedbackProvider>
  );
}

function ConsoleState({ children }: { children: ReactNode }): ReactElement {
  const { t } = useT();
  const feedback = useFeedback();
  const [sidebarCollapsed, setCollapsed] = useState<boolean>(() => loadCollapsed());
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [recordTitle, setRecordTitle] = useState<string | undefined>(undefined);
  const directoryQuery = useRef<string | undefined>(undefined);

  const toggleSidebar = useCallback((): void => {
    setCollapsed((c) => {
      try {
        window.localStorage.setItem(SIDEBAR_KEY, c ? '0' : '1');
      } catch {
        // persistence is best-effort
      }
      return !c;
    });
  }, []);

  const { toast: showToast, dismissToast, confirm } = feedback;
  const toast = useCallback(
    (spec: ToastSpec): void => {
      showToast(
        spec.onUndo !== undefined
          ? {
              ...spec,
              undoLabel: spec.undoLabel ?? t('ui.undo'),
              onUndo: () => {
                dismissToast();
                spec.onUndo?.();
              },
            }
          : spec,
      );
    },
    [showToast, dismissToast, t],
  );

  // ⌘K / Ctrl+K toggles the palette anywhere in the console.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo<ConsoleValue>(
    () => ({
      sidebarCollapsed,
      toggleSidebar,
      toast,
      confirm,
      paletteOpen,
      openPalette: () => setPaletteOpen(true),
      closePalette: () => setPaletteOpen(false),
      takeDirectoryQuery: () => {
        const q = directoryQuery.current;
        directoryQuery.current = undefined;
        return q;
      },
      setDirectoryQuery: (q) => {
        directoryQuery.current = q;
      },
      recordTitle,
      setRecordTitle,
    }),
    [sidebarCollapsed, toggleSidebar, toast, confirm, paletteOpen, recordTitle],
  );

  return <ConsoleContext.Provider value={value}>{children}</ConsoleContext.Provider>;
}

export function useConsole(): ConsoleValue {
  const value = useContext(ConsoleContext);
  if (value === undefined) throw new Error('useConsole must be used within a ConsoleProvider');
  return value;
}
